import { useEffect, useRef } from "react";
import { add } from "@amplitude/analytics-browser";
import { useAuth } from "../context/AuthContext";

/**
 * Attaches Amplitude Session Replay (which powers heatmaps) for NON-Indian
 * (i.e. USA / foreign) users only — Indian sessions are never recorded, to
 * keep replay volume/cost minimal.
 *
 * Why two signals: `userCountry` (geoapify) is only detected for guests
 * (AuthContext calls detectGuestLocation when there's no session), so a
 * returning logged-in USA user keeps the default 'IN'. Their `currency` is
 * 'USD' though, so we treat EITHER a foreign currency OR a detected foreign
 * country as "record". Guests in India have null currency + 'IN' country →
 * never recorded.
 *
 * The plugin is added at most once, the first time we can confidently tell the
 * user is non-Indian (recording starts from that point in the session).
 */
// Re-enabled after diagnosis: the "empty chat bubble" was NOT Session Replay —
// it was a data mismatch on a test account (country='IN' + currency='USD').
// Real USD users (country='US') record via Session Replay AND chat fine.
const SESSION_REPLAY_ENABLED = true;

export default function AmplitudeSessionReplay() {
  const { currency, userCountry } = useAuth();
  const addedRef = useRef(false);

  useEffect(() => {
    if (!SESSION_REPLAY_ENABLED) return;
    if (addedRef.current) return;

    const foreignByCurrency = !!currency && currency.toUpperCase() !== "INR";
    const foreignByCountry =
      !!userCountry && userCountry.toUpperCase() !== "IN";
    if (!foreignByCurrency && !foreignByCountry) return; // Indian / unknown

    addedRef.current = true;
    // Lazy-load the ~40kB replay plugin so only non-Indian users ever download
    // it (Indian sessions never touch this chunk).
    import("@amplitude/plugin-session-replay-browser")
      .then(({ sessionReplayPlugin }) => {
        add(
          sessionReplayPlugin({
            // Record all sessions among the (already USA-gated) users. Tunable.
            sampleRate: 1,
            privacyConfig: {
              // Blur elements tagged .amp-mask; fully drop elements tagged
              // .amp-block. (Inputs are masked by Session Replay's defaults;
              // the Razorpay checkout is a cross-origin iframe, never recorded.)
              maskSelector: [".amp-mask"],
              blockSelector: [".amp-block"],
            },
          })
        );
      })
      .catch((e) => {
        // Never let a replay-load failure affect the app.
        console.warn("[SessionReplay] failed to load", e);
      });
  }, [currency, userCountry]);

  return null;
}
